import type { AdminSlotAction, BotApi } from './BotApi';
import type {
  AuditEvent,
  AuditEventsPage,
  BotCommand,
  BotSlot,
  BotStatus,
  CommandRequest,
  ReconnectSettings,
  SaveSlotRequest,
  UserProfile
} from '../domain/types';
import { runtimeConfig } from '../config/runtimeConfig';

const config = runtimeConfig();
const localBaseUrl = config.localApiUrl;

async function localJson<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`${localBaseUrl}${path}`, {
    headers: { 'Content-Type': 'application/json' },
    ...init
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.error || `HTTP ${response.status}`);
  return data as T;
}

function mapSlot(slot: any): BotSlot {
  return {
    id: String(slot.id),
    slotKey: String(slot.id),
    label: String(slot.label || slot.id),
    userEmail: slot.linked_account_label || undefined,
    agentId: String(slot.agent_host_id || 'local'),
    licenseStatus: slot.license_status || 'inactive',
    licensePlan: slot.license_status === 'trial' || slot.plan_key === 'trial'
      ? 'trial'
      : slot.plan_key === 'pro_plus'
        ? 'pro_plus'
        : 'pro',
    planKey: slot.plan_key || null,
    linked: Boolean(slot.linked),
    state: slot.state || 'empty',
    enabledModules: {
      collect: slot.enabled_modules?.collect !== false,
      train_troops: slot.enabled_modules?.train_troops !== false,
      auto_hunt: slot.enabled_modules?.auto_hunt !== false,
      intelligence: slot.enabled_modules?.intelligence === true
    }
  };
}

function mapAuditEvent(event: any, index: number): AuditEvent {
  return {
    id: String(event.id || `${index}`),
    createdAt: String(event.timestamp || event.created_at || new Date().toISOString()),
    eventType: String(event.event_type || event.type || 'audit_event'),
    userId: event.user_id || null,
    slotId: event.slot_id || null,
    agentId: event.agent_id || null,
    payload: event.payload || {}
  };
}

function mapStatus(payload: any): BotStatus {
  const reconnectSettings = payload.reconnect_settings || null;
  const reconnect = payload.reconnect || null;
  const sessionStartedAt =
    typeof payload.session_started_at === 'number' ? new Date(payload.session_started_at * 1000).toISOString() : null;
  return {
    slotId: payload.active_slot_id,
    state: payload.agent_state || payload.mode || 'idle',
    running: Boolean(payload.running),
    lastResult: payload.last_result,
    nextCheckSeconds: payload.next_check_seconds,
    latestLogLine: payload.latest_log_line || (Array.isArray(payload.logs) ? payload.logs[payload.logs.length - 1] : null),
    recentLogs: Array.isArray(payload.recent_logs)
      ? payload.recent_logs
      : Array.isArray(payload.logs)
        ? payload.logs.slice(-6)
        : [],
    runtimeSeconds: payload.session_uptime_seconds,
    runtimeStartedAt: sessionStartedAt,
    nextCheckStartedAt: payload.next_check_started_at || null,
    updatedAt: new Date().toISOString(),
    screenshotUrl: payload.last_screenshot ? `${localBaseUrl}/screenshots/${encodeURIComponent(payload.last_screenshot)}` : null,
    linkingAssistant: payload.linking_assistant || null,
    screenBlocker: payload.screen_blocker || null,
    reconnect: reconnect
      ? {
          scheduled: Boolean(reconnect.scheduled),
          running: Boolean(reconnect.running),
          deadline: reconnect.deadline ?? null,
          remainingSeconds: reconnect.remaining_seconds ?? null,
          phase: reconnect.phase ?? null,
          result: reconnect.result ?? null
        }
      : null,
    reconnectSettings: reconnectSettings
      ? {
          autoEnabled: Boolean(reconnectSettings.auto_enabled),
          delaySeconds: Number(reconnectSettings.delay_seconds || 300)
        }
      : null,
    colonyQueues: (payload.training_queue_status || []).map((item: any) => ({
      queue: item.queue,
      action: item.status || 'unknown',
      remaining: item.remaining
    })),
    worldQueues: (payload.world_march_status || payload.march_status || []).map((item: any) => ({
      queue: item.queue,
      action: item.status || 'unknown',
      remaining: item.remaining
    }))
  };
}

export const localBotApi: BotApi = {
  mode: 'local',
  allowCommands: config.allowCommands,
  allowedCommandSlotKeys: config.allowedCommandSlotKeys,
  async getCurrentUser(): Promise<UserProfile> {
    return { id: 'local', email: 'local-admin', role: 'admin' };
  },
  async listSlots(): Promise<BotSlot[]> {
    const payload = await localJson<any>('/api/slots');
    return (payload.slots || []).map(mapSlot);
  },
  async getSlotStatus(): Promise<BotStatus | null> {
    const payload = await localJson<any>('/api/agent/status');
    return mapStatus(payload);
  },
  async listCommands(): Promise<BotCommand[]> {
    return [];
  },
  async sendCommand(request: CommandRequest): Promise<BotCommand> {
    if (!config.allowCommands) {
      throw new Error('Comandos reales desactivados en modo local seguro.');
    }
    await localJson('/api/agent/command', {
      method: 'POST',
      body: JSON.stringify({
        action: request.action,
        slot_id: request.slotId,
        ...request.payload
      })
    });
    return {
      commandId: `local_${Date.now()}`,
      action: request.action,
      status: 'completed',
      queuedAt: new Date().toISOString(),
      completedAt: new Date().toISOString()
    };
  },
  async saveReconnectSettings(_slotId: string, settings: ReconnectSettings): Promise<void> {
    await localJson('/api/reconnect/settings', {
      method: 'POST',
      body: JSON.stringify({
        auto_enabled: settings.autoEnabled,
        delay_seconds: settings.delaySeconds
      })
    });
  },
  async reconnectNow(): Promise<BotCommand> {
    await localJson('/api/reconnect/now', { method: 'POST', body: '{}' });
    return {
      commandId: `local_reconnect_${Date.now()}`,
      action: 'reconnect',
      status: 'completed',
      queuedAt: new Date().toISOString(),
      completedAt: new Date().toISOString()
    };
  },
  async getAdminOverview() {
    const payload = await localJson<any>('/api/admin/overview');
    return {
      users: (payload.users || []).map((user: any) => ({
        id: String(user.id),
        email: String(user.display_name || user.id),
        role: user.role || 'user'
      })),
      slots: (payload.slots || []).map(mapSlot),
      agentHosts: [
        {
          agentId: 'local',
          label: 'local',
          status: 'online',
          lastSeenAt: new Date().toISOString(),
          inventoryUpdatedAt: new Date().toISOString(),
          inventory: payload.bluestacks_inventory || {}
        }
      ],
      auditEvents: (payload.audit_events || []).slice(0, 20).map(mapAuditEvent)
    };
  },
  async getAuditEvents(options = {}): Promise<AuditEventsPage> {
    const limit = Math.max(1, Math.min(500, options.limit ?? 300));
    const offset = Math.max(0, options.offset ?? 0);
    const requestedLimit = offset + limit;
    const payload = await localJson<any>(`/api/audit/events?limit=${requestedLimit}`);
    const events = (payload.events || []).map(mapAuditEvent);
    const filteredEvents = options.slotId
      ? events.filter((event: AuditEvent) => event.slotId === options.slotId)
      : options.agentId
        ? events.filter((event: AuditEvent) => event.agentId === options.agentId)
      : events;
    const pageEvents = filteredEvents.slice(offset, offset + limit);
    return {
      events: pageEvents,
      limit,
      offset,
      total: filteredEvents.length,
      hasMore: offset + pageEvents.length < filteredEvents.length
    };
  },
  async saveSlot(request: SaveSlotRequest) {
    const payload = await localJson<any>('/api/admin/slots/update', {
      method: 'POST',
      body: JSON.stringify({
        slot_id: request.slotKey,
        changes: {
          label: request.label,
          owner_user_id: request.userId,
          agent_host_id: request.agentId,
          ...(request.regionId?.trim()
            ? {
              region_id: request.regionId.trim(),
              region_status: 'verified',
              region_source: 'admin',
              region_verified_at: new Date().toISOString()
            }
            : {}),
          license_status: request.licenseStatus,
          plan_key: request.licensePlan === 'pro_plus' ? 'pro_plus' : request.licensePlan,
          linked: request.linked,
          enabled_modules: request.enabledModules
        }
      })
    });
    return mapSlot(payload.slot);
  },
  async releaseSlot(slotId: string) {
    await localJson('/api/admin/slots/delete', {
      method: 'POST',
      body: JSON.stringify({ slot_id: slotId })
    });
  },
  async sendAdminSlotCommand(action: AdminSlotAction, payload: Record<string, unknown>) {
    const operationMap: Record<AdminSlotAction, string> = {
      admin_create_slot: '/api/admin/slots/create',
      admin_update_slot: '/api/admin/slots/update',
      admin_delete_slot: '/api/admin/slots/delete',
      admin_reset_slot: '/api/admin/slots/reset',
      admin_select_slot: '/api/slot/select',
      admin_start_hosted_workers: '',
      admin_stop_hosted_workers: '',
      admin_start_slot_worker: '',
      admin_stop_slot_worker: '',
      admin_reconcile_host_capacity: ''
    };
    if (!operationMap[action]) {
      throw new Error('Comando de workers hosted disponible solo desde el panel Supabase.');
    }
    await localJson(operationMap[action], { method: 'POST', body: JSON.stringify(payload) });
    return { commandId: `local_${Date.now()}` };
  }
};
